import * as React from 'react';
import ReactTable, { ReactTableDefaults } from 'react-table';
import {
  addTableAllFetchDataEventListener,
  addTableFetchDataEventListener,
  dispatchTableOnSelectionEvent,
  removeTableAllFetchDataEventListener,
  removeTableFetchDataEventListener,
} from '../tables/tableEvents';
import { LoadingSpinner } from '../thumbtack/spinner/LoadingSpinner';
import { HiddenNoDataComponent } from './custom_components/HiddenNoDataComponent';
import { HiddenThead } from './custom_components/HiddenThead';
import { NoDataComponent } from './custom_components/NoDataComponent';
import { PaginationComponent } from './custom_components/PaginationComponent';

interface ITableProps {
  data: any;
  className?: string;
  columns: any;
  identifier: string;
  selectable?: boolean;
  loading?: boolean;
  noDataComponent?: any;
  hiddenNoDataComponent?: any;
  loadingComponent?: any;
  getTheadGroupTrProps?: any;
  getTheadTrProps?: any;
  getTrProps?: any;
  getTrGroupProps?: any;
  showPagination?: boolean;
  pageSize?: number;
  hideHeader?: boolean;
  resizable?: boolean;
  fetchData?(): void;
  handleSelection?(selection: any): void;
  disableSelectForRow?(row: any): boolean;
}

interface ITableState {
  data: any;
  columns: any;
  selectAll: boolean;
  selection: any;
  selectable: boolean;
  filterText: string;
}

export class Table extends React.Component<ITableProps, ITableState> {
  public static defaultProps: Partial<ITableProps> = {
    className: '-unthemed',
    hiddenNoDataComponent: HiddenNoDataComponent,
    hideHeader: false,
    loading: false,
    loadingComponent: LoadingSpinner,
    noDataComponent: NoDataComponent,
    resizable: false,
    selectable: false,
    showPagination: false,
  };

  constructor(props: ITableProps) {
    super(props);
    this.state = {
      data: this.props.data,
      columns: this.props.columns,
      selectAll: false,
      selection: [],
      selectable: this.props.selectable,
      filterText: '',
    };
    this.handleSelection = this.handleSelection.bind(this);
    this.fetchData = this.fetchData.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
  }

  componentDidMount() {
    const { identifier } = this.props;
    document.addEventListener(`${this.props.identifier}:search`, this.handleFilterChange);
    addTableFetchDataEventListener(identifier, this.fetchData);
    addTableAllFetchDataEventListener(this.fetchData);
  }

  componentWillUnmount() {
    const { identifier } = this.props;
    document.removeEventListener(`${this.props.identifier}:search`, this.handleFilterChange);
    removeTableFetchDataEventListener(identifier, this.fetchData);
    removeTableAllFetchDataEventListener(this.fetchData);
  }

  fetchData() {
    if (this.props.fetchData) {
      this.setState({ selection: [], selectAll: false }, () => {
        this.handleSelection();
        this.props.fetchData();
      });
    }
  }

  isSelected(row) {
    return this.state.selection.includes(row.original);
  }

  deselectRow(row) {
    const selection = this.state.selection.filter((selection) => selection !== row.original);
    this.setState({ selection, selectAll: false }, () => this.handleSelection());
  }

  selectRow(row) {
    const selection = [...this.state.selection];
    selection.push(row.original);
    this.setState({ selection, selectAll: false }, () => this.handleSelection());
  }

  handleSelection() {
    const { identifier, handleSelection: handleSelectionProp } = this.props;
    const { selection } = this.state;
    dispatchTableOnSelectionEvent(identifier, selection);

    if (handleSelectionProp) {
      handleSelectionProp(selection);
    }
  }

  loading() {
    if (this.props.loading) {
      return this.props.loadingComponent;
    }
  }

  noData() {
    if (!this.props.loading) {
      return this.props.noDataComponent;
    }
    return this.props.hiddenNoDataComponent;
  }

  columns() {
    if (this.state.selectable) {
      const columns = [...this.props.columns];
      columns.unshift({
        Header: () => (
          <span className="pretty p-icon p-smooth mar-r-0">
            <input
              type="checkbox"
              checked={this.state.selectAll}
              onChange={(e) => {
                if (this.state.selectAll) {
                  this.setState({ selectAll: false, selection: [] }, () => this.handleSelection());
                } else {
                  let allSelectable = this.state.data;
                  if (this.props.disableSelectForRow) {
                    allSelectable = allSelectable.filter((row) => !this.props.disableSelectForRow(row));
                  }

                  this.setState({ selectAll: true, selection: allSelectable }, () => {
                    this.handleSelection();
                  });
                }
              }}
            />
            <span className="state p-primary">
              <i className="icon fa fa-check"></i>
              <label></label>
            </span>
          </span>
        ),
        // Max width is the checkbox size (16px) + 8px of padding either side. Needs to be 40 due to IE..
        maxWidth: 40,
        headerClassName: '-checkbox',
        className: '-checkbox',
        accessor: 'id',
        sortable: false,
        Cell: (row) => {
          return (
            <span className="pretty p-icon p-smooth mar-r-0">
              <input
                type="checkbox"
                onChange={(e) => {
                  if (this.isSelected(row)) {
                    this.deselectRow(row);
                  } else {
                    this.selectRow(row);
                  }
                }}
                checked={
                  !this.props.disableSelectForRow?.(row.original) && (this.state.selectAll || this.isSelected(row))
                }
                disabled={this.props.disableSelectForRow?.(row.original)}
              />
              <span className="state p-primary">
                <i className="icon fa fa-check"></i>
                <label></label>
              </span>
            </span>
          );
        },
      });
      return columns;
    }
    return this.props.columns;
  }

  // Always runs before render, so we're doing the filtering in here
  static getDerivedStateFromProps(nextProps, currentState) {
    const data = nextProps.data.filter((data) => {
      if (!data.attributes) {
        return data;
      }
      return Object.keys(data.attributes).some((key) => {
        if (data.attributes[key]) {
          return data.attributes[key].toString().toLowerCase().includes(currentState.filterText);
        }
        return false;
      });
    });
    return { data };
  }

  handleFilterChange(event) {
    const filterText = event.detail.searchTerm.toLowerCase();
    this.setState({ filterText });
  }

  pageSize() {
    if (!!this.props.pageSize) {
      if (this.props.pageSize > this.state.data.length) {
        return this.state.data.length;
      }
      return this.props.pageSize;
    }
    return this.state.data.length;
  }

  showPagination() {
    if (this.props.showPagination) {
      if (this.state.data.length > this.props.pageSize) {
        return true;
      }
      return false;
    }
    return false;
  }

  tableHeadComponent() {
    if (this.props.hideHeader) {
      return HiddenThead;
    }
    return ReactTableDefaults['TheadComponent'];
  }

  render() {
    return (
      <div>
        <ReactTable
          data={this.state.data}
          columns={this.columns()}
          pageSize={this.pageSize()}
          showPagination={this.showPagination()}
          PaginationComponent={PaginationComponent}
          NoDataComponent={this.noData()}
          LoadingComponent={this.loading()}
          TheadComponent={this.tableHeadComponent()}
          getTheadTrProps={this.props.getTheadTrProps}
          getTrGroupProps={this.props.getTrGroupProps}
          getTrProps={this.props.getTrProps}
          minRows={0}
          className={this.props.className}
          resizable={this.props.resizable}
        />
      </div>
    );
  }
}
